import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import {
  getAllBranch,
  getAllRoutes,
  getAllRoutesByCompany,
} from "../../services";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { CHANNEL_FILTER, DOC_TYPE_FILTER, ETAX_STATUS_FILTER } from "./documentMeta";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import DateRangePicker, {
  etaxPresetGroups,
} from "../common/DateRangePicker";

interface DocumentFilterProps {
  documentType: string;
  setDocumentType: Dispatch<SetStateAction<string>>;
  etaxStatus: string;
  setEtaxStatus: Dispatch<SetStateAction<string>>;
  branchCode: string;
  setBranchCode: Dispatch<SetStateAction<string>>;
  routeCode: string;
  setRouteCode: Dispatch<SetStateAction<string>>;
  customerName: string;
  setCustomerName: Dispatch<SetStateAction<string>>;
  taxId: string;
  setTaxId: Dispatch<SetStateAction<string>>;
  /** Pass all three to show the ช่วงวันที่ control; omit them on screens whose range is
   * fixed by the URL they were opened from. */
  dateFrom?: Dayjs;
  dateTo?: Dayjs;
  onChangeDateRange?: (from: Dayjs, to: Dayjs) => void;
  onChange: () => void;
  showStatusFilter?: boolean;
  /** Pass both to show the ช่องทาง control; omit on a screen already scoped to one
   * channel by the URL it was opened from, where a control could only contradict it. */
  channel?: string;
  setChannel?: Dispatch<SetStateAction<string>>;
  /** The choices of the ประเภท control; the bills' by default. */
  documentTypeOptions?: { label: string; value: string }[];
  /** The choices of the สถานะ control; the bills' by default. */
  statusOptions?: { label: string; value: string }[];
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
    <Grid container alignItems="center" spacing={1}>
      <Grid size={3}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Grid>
      <Grid size={9}>{children}</Grid>
    </Grid>
  </Grid>
);

export default function DocumentFilter({
  documentType,
  setDocumentType,
  etaxStatus,
  setEtaxStatus,
  branchCode,
  setBranchCode,
  routeCode,
  setRouteCode,
  customerName,
  setCustomerName,
  taxId,
  setTaxId,
  dateFrom,
  dateTo,
  onChangeDateRange,
  onChange,
  showStatusFilter = true,
  channel,
  setChannel,
  documentTypeOptions = DOC_TYPE_FILTER,
  statusOptions = ETAX_STATUS_FILTER,
}: DocumentFilterProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const presetGroups = useMemo(() => etaxPresetGroups(), []);

  const handleChangeDateRange = (from: Dayjs, to: Dayjs) => {
    onChangeDateRange?.(from, to);
    onChange();
  };

  const [branches, setBranches] = useState<Branch[]>([]);
  const [saleRoutes, setSaleRoutes] = useState<SaleRoute[]>([]);

  const getBranches = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await getAllBranch();
      setBranches(data);
    } catch {
      setBranches([]);
    }
  }, []);

  const getRoutes = useCallback(async () => {
    try {
      if (isHeadOfficeAccounting) {
        // HO picks a branch first; that branch's routes are then loaded on demand
        // by its company_id (mirrors the Sales / Sale Report filters which key
        // routes by company_id rather than filtering a company-wide list).
        if (branchCode === "all") {
          setSaleRoutes([]);
          return;
        }
        const companyId = branches.find(
          (branch) => branch.branch_code === branchCode,
        )?.company_id;
        if (!companyId) {
          setSaleRoutes([]);
          return;
        }
        const {
          data: { data },
        } = await getAllRoutes(companyId);
        setSaleRoutes(Array.isArray(data) ? data : data.rows ?? []);
      } else {
        // Branch users are already scoped by the API, so the company-wide route
        // list is exactly their branch's routes.
        const {
          data: { data },
        } = await getAllRoutesByCompany();
        setSaleRoutes(data);
      }
    } catch {
      setSaleRoutes([]);
    }
  }, [isHeadOfficeAccounting, branchCode, branches]);

  useEffect(() => {
    // HO accounting needs the branch list to resolve the company_id used when
    // loading a selected branch's routes.
    if (isHeadOfficeAccounting) getBranches();
  }, [isHeadOfficeAccounting, getBranches]);

  useEffect(() => {
    getRoutes();
  }, [getRoutes]);

  const handleChangeBranch = (value: string) => {
    setBranchCode(value);
    // Routes depend on the branch, so reset the route when the branch changes.
    setRouteCode("all");
    onChange();
  };

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField label="ประเภท">
        <TextField
          select
          size="small"
          fullWidth
          value={documentType}
          onChange={(e) => {
            setDocumentType(e.target.value);
            onChange();
          }}
        >
          {documentTypeOptions.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      {channel !== undefined && setChannel && (
        <FilterField label="ช่องทาง">
          <TextField
            select
            size="small"
            fullWidth
            value={channel}
            onChange={(e) => {
              setChannel(e.target.value);
              onChange();
            }}
          >
            {CHANNEL_FILTER.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      {showStatusFilter && (
        <FilterField label="สถานะ">
          <TextField
            select
            size="small"
            fullWidth
            value={etaxStatus}
            onChange={(e) => {
              setEtaxStatus(e.target.value);
              onChange();
            }}
          >
            {statusOptions.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      {isHeadOfficeAccounting && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={branchCode}
            onChange={(e) => handleChangeBranch(e.target.value)}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.branch_code}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          value={routeCode}
          onChange={(e) => {
            setRouteCode(e.target.value);
            onChange();
          }}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {saleRoutes.map((item) => (
            <MenuItem key={item.uuid} value={item.route_code}>
              {item.route_code} {item.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>

      <FilterField label="ชื่อลูกค้า">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอกชื่อลูกค้า"
          value={customerName}
          onChange={(e) => {
            setCustomerName(e.target.value);
            onChange();
          }}
        />
      </FilterField>

      {dateFrom && dateTo && onChangeDateRange && (
        <FilterField label="ช่วงวันที่">
          <DateRangePicker
            dateFrom={dateFrom}
            dateTo={dateTo}
            presetGroups={presetGroups}
            onChange={handleChangeDateRange}
          />
        </FilterField>
      )}

      <FilterField label="Tax ID">
        <TextField
          size="small"
          fullWidth
          placeholder="กรอก Tax ID"
          value={taxId}
          onChange={(e) => {
            setTaxId(e.target.value);
            onChange();
          }}
        />
      </FilterField>
    </Grid>
  );
}
